import { isValidElement, type ReactNode } from 'react';
import { STRAIGHT_ANSWERS } from '../home/StraightAnswers';
import { SAT_ABOUT_ANSWERS } from '../landing/SatAboutAnswers';
import { STUDENTS_ANSWERS } from '../landing/StudentsLandingPage';
import { PARENTS_ANSWERS } from '../landing/ParentsLandingPage';
import { TUTORS_ANSWERS } from '../landing/TutorsLandingPage';

export interface FaqEntry {
  q: string;
  a: ReactNode;
}

export interface FaqGroup {
  id: string;
  title: string;
  entries: FaqEntry[];
}

const norm = (q: string) => q.trim().toLowerCase();

/** Questions left out of the FAQ on purpose. */
const EXCLUDED = new Set([
  // Owner-pending pricing promise (GitHub issue #307). "Is it really free?" covers it for now.
  norm('Is the course free forever?'),
]);

const pick = (list: readonly FaqEntry[], questions: readonly string[]): FaqEntry[] =>
  questions.map((q) => {
    const hit = list.find((e) => norm(e.q) === norm(q));
    if (!hit) throw new Error(`FAQ source question not found: ${q}`);
    return hit;
  });

/**
 * The FAQ is the existing "Straight answers" blocks (home, the SAT course page, and the student,
 * parent and tutor pages), grouped and de-duplicated by question. The first occurrence wins, so a
 * question that appears on two pages is answered once, in the wording of the more specific page.
 */
export function buildFaqGroups(): FaqGroup[] {
  const course = [
    ...pick(STRAIGHT_ANSWERS, ['Is it really free?']),
    ...pick(SAT_ABOUT_ANSWERS, ['Do I need an account?', 'How long is the diagnostic?', 'Are these real SAT questions?']),
    ...pick(STUDENTS_ANSWERS, ['Do I need a tutor to use the SAT course?']),
  ];
  const tutoring = [
    ...pick(STRAIGHT_ANSWERS, ['What does Mentara sell?']),
    ...pick(SAT_ABOUT_ANSWERS, ['What does a tutor add?']),
    ...pick(STUDENTS_ANSWERS, [
      'Can I pick my tutor?',
      'What does tutoring cost?',
      'What subjects can I get help with?',
      'Can my parent see my sessions?',
    ]),
  ];
  const parents = [...PARENTS_ANSWERS];
  const tutors = [...TUTORS_ANSWERS];

  const seen = new Set<string>(EXCLUDED);
  const dedupe = (entries: readonly FaqEntry[]) =>
    entries.filter((e) => {
      const k = norm(e.q);
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    });

  return [
    { id: 'course', title: 'The free SAT course', entries: dedupe(course) },
    { id: 'tutoring', title: 'Tutoring', entries: dedupe(tutoring) },
    { id: 'parents', title: 'For parents', entries: dedupe(parents) },
    { id: 'tutors', title: 'For tutors', entries: dedupe(tutors) },
  ].filter((g) => g.entries.length > 0);
}

/** Plain text of an answer, for the FAQPage structured data. */
export function answerText(node: ReactNode): string {
  if (node == null || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(answerText).join('');
  if (isValidElement(node)) return answerText((node.props as { children?: ReactNode }).children);
  return '';
}
